import { useEffect, useState } from 'react';
import { useDashboardData } from './useDashboard';
import userProfileStore from '../core/UserProfileStore';
import useUserSettingStore from './UserSettingStore';
import { APP_ENV , WIDGET_DEFAULT_COLOR} from '../../constants';
import { accountingARUrl, accountingARAppLinkUrl } from './DashboardConfig';
import {useARDashboardFilter, getLastStatementDate} from './filters/ARFilter';
import moment from 'moment';

export const AR_DASHBOARD = {
  DASHBOARD_ID: 'ar-dashboard',
  DOMAIN_ID: 'accounting',
  DASHBOARD_PAYMENT_ID: 'ar-payment-dashboard',
  DASHBOARD_AGING_ID: 'ar-aging-dashboard',
  WIDGET_AR_PAYMENT_ID: 'ar-payment',
  WIDGET_AR_PAYMENT_AGING_ID: 'ar-aging',
};

export const getWidgets = (t:any) => {
return [
  {
    id: AR_DASHBOARD.WIDGET_AR_PAYMENT_ID,
    dashboardId: AR_DASHBOARD.DASHBOARD_ID,
    isFilterAvailable: true,
    link: accountingARAppLinkUrl[
      APP_ENV as unknown as keyof typeof accountingARAppLinkUrl
    ],
    title: t('dashboard.accounting.arPayment'),
    xKey: 'name',
    valueKey: 'Amount',
    isPricing: true,
    availableViews: ['line', 'bar', 'pie', 'table', 'grid'],
    defaultView: 'bar',
    percentageBase: 'Charges',
    thresholdFields: [
      { name: 'Charges' },
      { name: 'Payment Received' },
      { name: 'Balance Due' },
    ],
    dataMapping: {
      transform: (sourceData: any) => {
        return [
          {
            name: 'Charges',
            Amount: sourceData[0]?.agingSummaryTotals?.totalCharges,
            color: WIDGET_DEFAULT_COLOR,
          },
          {
            name: 'Payment Received',
            Amount: Math.abs(
              sourceData[0]?.agingSummaryTotals?.totalPaymentReceived
            ),
            color: WIDGET_DEFAULT_COLOR,
          },
          {
            name: 'Balance Due',
            Amount: sourceData[0]?.agingSummaryTotals?.totalBalDue,
            color: WIDGET_DEFAULT_COLOR,
          },
        ];
      },
    },
  },
  {
    id: AR_DASHBOARD.WIDGET_AR_PAYMENT_AGING_ID,
    dashboardId: AR_DASHBOARD.DASHBOARD_ID,
    isPricing: true,
    isFilterAvailable: true,
    title: t('dashboard.accounting.arAging'),
    link: accountingARAppLinkUrl[
      APP_ENV as unknown as keyof typeof accountingARAppLinkUrl
    ],
    xKey: 'name',
    valueKey: 'Amount',
    availableViews: ['line', 'bar', 'pie', 'table', 'grid'],
    defaultView: 'bar',
    thresholdFields: [
      { name: 'Current' },
      { name: '31 - 60d' },
      { name: '61 - 90d' },
      { name: '91 - 120d' },
      { name: '121d+' },
    ],
    dataMapping: {
      transform: (sourceData: any) => {
        return [
          {
            name: 'Current',
            Amount: sourceData[0]?.agingSummaryTotals?.totalCurrent,
            color: WIDGET_DEFAULT_COLOR,
          },
          {
            name: '31 - 60d',
            Amount: sourceData[0]?.agingSummaryTotals?.totalOver30,
            color: WIDGET_DEFAULT_COLOR,
          },
          {
            name: '61 - 90d',
            Amount: sourceData[0]?.agingSummaryTotals?.totalOver60,
            color: WIDGET_DEFAULT_COLOR,
          },
          {
            name: '91 - 120d',
            Amount: sourceData[0]?.agingSummaryTotals?.totalOver90,
            color: WIDGET_DEFAULT_COLOR,
          },
          {
            name: '121d+',
            Amount: sourceData[0]?.agingSummaryTotals?.totalOver120,
            color: WIDGET_DEFAULT_COLOR,
          },
        ];
      },
    },
  },
];
};

export const getARDashboardConfig = (
  franchise: string,
  primaryStore:string,
  storeIds: string[],
  enterpriseId: any,
  scheduleCodes: string[],
  date: string,
  widgetId: string,
  isLastStatementDate: boolean = false
) => {
  const config = [];

  const queryParams: any = {
    stores: storeIds,
    scheduleCodes: scheduleCodes,
  };
  
  // Add isLastStatementDate flag when Last Statement is selected
  if (isLastStatementDate) {
    queryParams.isLastStatementDate = true;
  }

  config.push({
    id: widgetId === AR_DASHBOARD.WIDGET_AR_PAYMENT_ID ? 'arDashBoardPayment' : 'arDashBoardAging',
    apiConfig: {
      method: 'GET',
      headers: {
        'x-franchise-id': franchise,
        'x-department-name': 'ACCOUNTING',
        'x-department-type': 'A',
        'x-store-id': primaryStore,
        'x-enterprise-id': enterpriseId,
      },
      queryParams: queryParams,
      path: '/accounts-receivable/arDashBoard/' + date,
      endpoint:
        accountingARUrl[APP_ENV as unknown as keyof typeof accountingARUrl],
    },
  });
  return config;
};

export const useARDashboard = ({ enabled = true, filteredWidgets = [] }: any = {}) => {

  const dashboardId = AR_DASHBOARD.DASHBOARD_ID;

  const { userSetting } = useUserSettingStore();
  const { enterpriseId } = userProfileStore();
  
  // Single shared filter for both widgets
  const filterData = useARDashboardFilter({
    userSetting,
    widgetId: AR_DASHBOARD.WIDGET_AR_PAYMENT_ID,
    enabled,
  });
  
  const [config, setConfig] = useState<any>(null);
  const [widgets, setWidgets] = useState<any[]>(filteredWidgets);
  const [lastStatementFetchStarted, setLastStatementFetchStarted] = useState(false);

  // Single Last Statement API config for both widgets
  const [lastStatementConfig, setLastStatementConfig] = useState<any>(null);

  // Single data fetch that serves both widgets
  const dashboardData = useDashboardData({
    config: config,
    enabled: enabled && !!config,
  });
  
  // Single Last Statement API data fetch
  const lastStatementData = useDashboardData({
    config: lastStatementConfig,
    enabled: enabled && !!lastStatementConfig,
  });

  // Detect if we need Last Statement date (reportDaysAgo=0 without explicit date)
  useEffect(() => {
    if (!enabled) {
      setLastStatementFetchStarted(false);
      setLastStatementConfig(null);
      return;
    }
    const filterSetting = filterData.filterSetting;
    
    if (!filterSetting || !enterpriseId)
      return;

    const { franchise, storeIds, primaryStore, reportDaysAgo, reportDate } = filterSetting;
    
    // Convert reportDaysAgo to number since it may come as a string
    const daysAgo = Number(reportDaysAgo);
    
    // If reportDaysAgo is 0 (Last Statement) and no explicit reportDate, fetch Last Statement date
    if (daysAgo === 0 && !reportDate && franchise && primaryStore && storeIds) {
      setLastStatementFetchStarted(false);
      setLastStatementConfig(
        getLastStatementDate(franchise, primaryStore, storeIds, enterpriseId)
      );
    } else {
      // Reset if conditions no longer met
      setLastStatementFetchStarted(false);
      setLastStatementConfig(null);
    }
  }, [enabled,filterData?.filterSetting, enterpriseId]);

  useEffect(() => {
    if (lastStatementData.isLoading) {
      setLastStatementFetchStarted(true);
    }
  }, [lastStatementData.isLoading]);
  
  // Build single API config using shared filter
  useEffect(() => {
    if (!enabled || !filterData.filterSetting || !enterpriseId) {
      setConfig(null);
      return;
    }
    const filterSetting = filterData.filterSetting;
    const { franchise, storeIds, primaryStore, scheduleCodes } = filterSetting;

    // Convert reportDaysAgo to number since it may come as a string
    const daysAgo = Number(filterSetting.reportDaysAgo);
    
    // If reportDaysAgo=0 (Last Statement) without explicit reportDate, we need Last Statement date
    // Don't build config until we have it
    if (daysAgo === 0 && !filterSetting.reportDate) {
      if (!lastStatementData?.rawData?.[0]) {
        return; // Wait for Last Statement API response
      }
    }
    
    // Calculate date: use reportDate if available, or Last Statement API date, otherwise calculate from reportDaysAgo
    let date;
    let isLastStatementDate = false;
    
    if (filterSetting.reportDate) {
      date = filterSetting.reportDate;
    } else if (daysAgo === 0 && lastStatementData?.rawData?.[0]) {
      // Use Last Statement date from API (date is directly in rawData[0])
      date = lastStatementData.rawData[0];
      isLastStatementDate = true; // Mark as Last Statement
    } else if (daysAgo === 1) {
      // Today
      date = moment().format('YYYY-MM-DD');
    } else if (daysAgo > 0) {
      // X days ago: subtract the exact number of days
      date = moment().subtract(daysAgo, 'days').format('YYYY-MM-DD');
    } else {
      // Default to today's date if no settings
      date = moment().format('YYYY-MM-DD');
    }

    // Build single config that will be used for both widgets
    setConfig(
      getARDashboardConfig(
        franchise,
        primaryStore,
        storeIds,
        enterpriseId,
        scheduleCodes,
        date,
        AR_DASHBOARD.WIDGET_AR_PAYMENT_ID, // Widget ID doesn't matter anymore for API
        isLastStatementDate
      )
    );
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled,filterData.filterSetting, enterpriseId, lastStatementData?.rawData?.[0]]);

  // Initialize widgets
  useEffect(() => {
    if (!userSetting) return;
    const dashboard = userSetting.dashboard?.find((d: any) => d.id === dashboardId);
    const savedWidgets = dashboard?.widgets || [];
    const updatedWidgets = filteredWidgets
      .map((dwidget: any) => {
      const saved = savedWidgets.find((w: any) => w.id === dwidget.id);
      return {
        ...dwidget,
        dashboardId,
        fields: saved?.fields || dwidget.fields,
        view: saved?.view || dwidget.view,
      };
    });
    setWidgets(updatedWidgets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSetting, dashboardId, filteredWidgets]);

  // Helper function - both widgets now use the same shared data
  const getWidgetData = (widget: any) => {
    return dashboardData.getWidgetData(widget);
  };

  // Get the complete filter from userSetting
  const dashboardFilter = userSetting?.dashboard?.find((d: any) => d.id === dashboardId)?.filter || {};

  const filterSetting = filterData.filterSetting;
  const requiresLastStatementDate =
    enabled &&
    !!filterSetting &&
    !!enterpriseId &&
    Number(filterSetting.reportDaysAgo) === 0 &&
    !filterSetting.reportDate;

  const isPreparingLastStatement =
    requiresLastStatementDate && (!lastStatementConfig || !lastStatementFetchStarted);

  const isLoading =
    enabled &&
    (filterData.isLoading ||
      isPreparingLastStatement ||
      lastStatementData.isLoading ||
      dashboardData.isLoading);

  return {
    dashboardId,
    widgets,
    filter: dashboardFilter,
    isLoading,
    error: dashboardData.error || filterData.error,
    getWidgetData,
    filterSetting,
    userSetting
  };
};
